import { useState } from 'react';
import {
  Flex,
  Grid,
  GridItem,
  FormControl,
  FormErrorMessage,
  Input,
  Checkbox,
  Button,
  Text,
  VStack,
  HStack,
  useDisclosure,
  IconButton,
  Tooltip,
  Spinner,
  Divider,
} from '@chakra-ui/react';
import {
  AddIcon,
  DeleteIcon,
  DownloadIcon,
  ViewIcon,
  EditIcon,
} from '@chakra-ui/icons';
import { useMutation } from '@tanstack/react-query';

import {
  apiCreateQuoteConsent,
  apiUpdateAutoQuotePolicyInfo,
  apiOrderDashReport,
  apiPopulateFields,
  getDashReportPdfUrl,
  type DashReportDriver,
} from 'api';
import DashConsentModal, {
  CONSENT_TYPE_DASH_HISTORY,
} from '../../components/DashConsentModal';
import SectionCard from '../../components/SectionCard';
import Storage from 'storage';
import { createEmptyDriver } from '../../constants/policyOptions';
import { LicenseNumberInput } from 'components/LicenseNumberInput';
import { validateLicenseNumber } from 'utils/licenseNumber';
import { useMarketplaceToast } from '../../hooks';
import type { DashReportSectionProps } from './types';

const DashReportSection = ({
  quoteId,
  province,
  drivers,
  onDriversChange,
  onVehiclesPopulated,
}: DashReportSectionProps) => {
  const toast = useMarketplaceToast();
  const {
    isOpen: isConsentModalOpen,
    onOpen: onConsentModalOpen,
    onClose: onConsentModalClose,
  } = useDisclosure();
  const [selectedDriverIndex, setSelectedDriverIndex] = useState<number | null>(
    null,
  );
  const [orderingDriverIndex, setOrderingDriverIndex] = useState<number | null>(
    null,
  );
  const [populatingDriverIndex, setPopulatingDriverIndex] = useState<
    number | null
  >(null);

  const saveDriversMutation = useMutation({
    mutationFn: (updatedDrivers: DashReportDriver[]) =>
      apiUpdateAutoQuotePolicyInfo(quoteId, {
        full_history_dash_dash_report_drivers: updatedDrivers,
      }),
    onSuccess: (response) => {
      const updatedDrivers =
        response.policy_info?.full_history_dash_dash_report_drivers || [];
      if (updatedDrivers.length > 0) {
        onDriversChange(updatedDrivers);
      }
    },
    onError: () => {
      toast.error('Failed to save');
    },
  });

  const consentMutation = useMutation({
    mutationFn: (params: { method: number; licenseNumber: string }) =>
      apiCreateQuoteConsent(quoteId, {
        type: CONSENT_TYPE_DASH_HISTORY,
        method: params.method,
        license_number: params.licenseNumber,
      }),
    onSuccess: (response) => {
      if (selectedDriverIndex !== null) {
        const updated = [...drivers];
        updated[selectedDriverIndex] = {
          ...updated[selectedDriverIndex],
          consent_given: true,
          consent_id: response.id,
        };
        onDriversChange(updated);
        saveDriversMutation.mutate(updated);
      }
      onConsentModalClose();
      setSelectedDriverIndex(null);
      toast.success('Consent recorded');
    },
    onError: () => {
      toast.error('Failed to record consent');
    },
  });

  const orderDashMutation = useMutation({
    mutationFn: (licenseNumber: string) =>
      apiOrderDashReport(quoteId, licenseNumber),
    onSuccess: (response) => {
      if (response.drivers) {
        onDriversChange(response.drivers);
      }

      if (response.success) {
        const addedCount = response.added_drivers?.length || 0;
        const message = response.cached
          ? 'DASH report retrieved from cache'
          : 'DASH report ordered';
        const description =
          addedCount > 0
            ? `${addedCount} driver(s) added from prior policy`
            : undefined;

        toast.success(message, { description });
      } else {
        const licenseNum = response.driver?.license_number || '';
        toast.warning(
          `No DASH report available for the license number ${licenseNum}`,
          { duration: 5000 },
        );
      }
      setOrderingDriverIndex(null);
    },
    onError: () => {
      toast.error('Failed to order DASH report');
      setOrderingDriverIndex(null);
    },
  });

  const populateFieldsMutation = useMutation({
    mutationFn: (licenseNumber: string) =>
      apiPopulateFields(quoteId, licenseNumber),
    onSuccess: (response) => {
      if (response.success) {
        toast.success(response.message);
        onVehiclesPopulated?.();
      } else {
        toast.warning('Failed to populate fields', {
          description: response.error,
        });
      }
      setPopulatingDriverIndex(null);
    },
    onError: () => {
      toast.error('Failed to populate fields');
      setPopulatingDriverIndex(null);
    },
  });

  const handlePopulateFields = (index: number) => {
    const driver = drivers[index];
    if (driver.status !== 'available' || !driver.license_number) {
      return;
    }
    setPopulatingDriverIndex(index);
    populateFieldsMutation.mutate(driver.license_number);
  };

  const handleConsentClick = (index: number) => {
    const driver = drivers[index];
    if (driver.consent_given) {
      return;
    }
    setSelectedDriverIndex(index);
    onConsentModalOpen();
  };

  const handleConsentConfirm = (method: number) => {
    if (selectedDriverIndex !== null) {
      const driver = drivers[selectedDriverIndex];
      consentMutation.mutate({
        method,
        licenseNumber: driver.license_number,
      });
    }
  };

  const handleOrderDash = (index: number) => {
    const driver = drivers[index];
    if (!driver.consent_given || !driver.license_number) {
      return;
    }
    setOrderingDriverIndex(index);
    orderDashMutation.mutate(driver.license_number);
  };

  const handleViewDash = (driver: DashReportDriver) => {
    if (!driver.dash_log_id) {
      return;
    }
    const endpoint = Storage.get('API_ENDPOINT') || '';
    const token = Storage.get('user')?.token || '';
    const url = `${endpoint}${getDashReportPdfUrl(quoteId, driver.dash_log_id)}`;
    window.open(`${url}?user=${token}`, '_blank');
  };

  const handleAddDriver = () => {
    const updated = [...drivers, createEmptyDriver()];
    onDriversChange(updated);
  };

  const handleRemoveDriver = (index: number) => {
    if (drivers.length > 1) {
      const updated = drivers.filter((_, i) => i !== index);
      onDriversChange(updated);
    }
  };

  const handleDriverChange = (
    index: number,
    field: keyof DashReportDriver,
    value: any,
  ) => {
    const updated = [...drivers];
    updated[index] = { ...updated[index], [field]: value };
    onDriversChange(updated);
  };

  return (
    <>
      <SectionCard mb={4}>
        <Flex justify="space-between" align="center" mb={1}>
          <Text
            fontSize="xs"
            fontWeight="700"
            textTransform="uppercase"
            letterSpacing="wide"
            color="gray.500"
          >
            DASH Report
          </Text>
          <Button
            size="xs"
            variant="outline"
            leftIcon={<AddIcon />}
            onClick={handleAddDriver}
          >
            Add Driver
          </Button>
        </Flex>

        <Grid
          templateColumns={{ base: '1fr', md: 'repeat(12, 1fr)' }}
          gap={3}
          display={{ base: 'none', md: 'grid' }}
        >
          <GridItem colSpan={3}>
            <Text fontSize="xs" fontWeight="600">
              Driver
            </Text>
          </GridItem>
          <GridItem colSpan={4}>
            <Text fontSize="xs" fontWeight="600">
              License Number
            </Text>
          </GridItem>
        </Grid>

        <VStack spacing={0} align="stretch" divider={<Divider />}>
          {drivers.map((driver, index) => (
            <Grid
              key={index}
              templateColumns={{ base: '1fr', md: 'repeat(12, 1fr)' }}
              gap={4}
              py={2}
              alignItems="center"
            >
              <GridItem colSpan={{ base: 1, md: 3 }}>
                <Text
                  fontSize="xs"
                  fontWeight="600"
                  mb={1}
                  display={{ base: 'block', md: 'none' }}
                >
                  Driver
                </Text>
                <Flex gap={2} align="center">
                  {drivers.length > 1 && (
                    <Tooltip label="Remove driver">
                      <IconButton
                        aria-label="Remove driver"
                        icon={<DeleteIcon />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleRemoveDriver(index)}
                        isDisabled={driver.consent_given}
                      />
                    </Tooltip>
                  )}
                  <Input
                    size="sm"
                    value={driver.name}
                    onChange={(e) =>
                      handleDriverChange(index, 'name', e.target.value)
                    }
                    variant="main"
                    placeholder="Full name"
                    isDisabled={driver.consent_given}
                  />
                </Flex>
              </GridItem>

              <GridItem colSpan={{ base: 1, md: 4 }}>
                {(() => {
                  const validation = validateLicenseNumber(
                    driver.license_number,
                    province,
                  );
                  return (
                    <FormControl isInvalid={!validation.valid}>
                      <Text
                        fontSize="xs"
                        fontWeight="600"
                        mb={1}
                        display={{ base: 'block', md: 'none' }}
                      >
                        License Number
                      </Text>
                      <LicenseNumberInput
                        size="xs"
                        value={driver.license_number}
                        provinceId={province}
                        onChange={(value) =>
                          handleDriverChange(index, 'license_number', value)
                        }
                        variant="main"
                        isDisabled={driver.consent_given}
                      />
                      {!validation.valid && (
                        <FormErrorMessage fontSize="xs">
                          {validation.error}
                        </FormErrorMessage>
                      )}
                    </FormControl>
                  );
                })()}
              </GridItem>

              <GridItem colSpan={{ base: 1, md: 3 }}>
                <Checkbox
                  isChecked={driver.consent_given}
                  onChange={() => handleConsentClick(index)}
                  colorScheme="blue"
                  isDisabled={
                    driver.consent_given ||
                    consentMutation.isPending ||
                    !validateLicenseNumber(driver.license_number, province)
                      .valid
                  }
                >
                  <Text fontSize="xs">Client Consent To Pull DASH</Text>
                </Checkbox>
              </GridItem>

              <GridItem colSpan={{ base: 1, md: 2 }}>
                <HStack spacing={1} justify="end">
                  {driver.status === 'none' ? (
                    <Button size="xs" variant="outline" isDisabled>
                      NONE
                    </Button>
                  ) : (
                    <Tooltip label="Order DASH">
                      <IconButton
                        aria-label="Order DASH"
                        icon={
                          orderingDriverIndex === index ? (
                            <Spinner size="xs" />
                          ) : (
                            <DownloadIcon />
                          )
                        }
                        size="xs"
                        colorScheme="blue"
                        isDisabled={
                          !driver.consent_given ||
                          driver.status === 'available' ||
                          orderDashMutation.isPending
                        }
                        onClick={() => handleOrderDash(index)}
                      />
                    </Tooltip>
                  )}
                  <Tooltip label="View DASH">
                    <IconButton
                      aria-label="View DASH"
                      icon={<ViewIcon />}
                      size="xs"
                      variant="outline"
                      isDisabled={driver.status !== 'available'}
                      onClick={() => handleViewDash(driver)}
                    />
                  </Tooltip>
                  <Tooltip label="Populate Fields">
                    <IconButton
                      aria-label="Populate Fields"
                      icon={
                        populatingDriverIndex === index ? (
                          <Spinner size="xs" />
                        ) : (
                          <EditIcon />
                        )
                      }
                      size="xs"
                      variant="outline"
                      isDisabled={
                        driver.status !== 'available' ||
                        populateFieldsMutation.isPending
                      }
                      onClick={() => handlePopulateFields(index)}
                    />
                  </Tooltip>
                </HStack>
              </GridItem>
            </Grid>
          ))}
        </VStack>
      </SectionCard>

      <DashConsentModal
        isOpen={isConsentModalOpen}
        onClose={() => {
          onConsentModalClose();
          setSelectedDriverIndex(null);
        }}
        onConfirm={handleConsentConfirm}
        isLoading={consentMutation.isPending || saveDriversMutation.isPending}
        driverName={
          selectedDriverIndex !== null ? drivers[selectedDriverIndex]?.name : ''
        }
        licenseNumber={
          selectedDriverIndex !== null
            ? drivers[selectedDriverIndex]?.license_number
            : ''
        }
      />
    </>
  );
};

export default DashReportSection;
